import React from 'react';
import { Transaction } from '@tcs/client-portal-api/types/Transaction';
import Table from '@tcs/client-portal-common/components/molecules/Table';
import transformDebitCredit from '@tcs/client-portal-common/utils/transformDebitCredit';
import BaseHeader from '@tcs/client-portal-common/components/molecules/Table/cells/headers/BaseHeader';
import TwoLineCell from '@tcs/client-portal-common/components/molecules/Table/cells/TwoLineCell';
import DateCell from '@tcs/client-portal-common/components/molecules/Table/cells/DateCell';
import DebitCreditCell from '@tcs/client-portal-common/components/molecules/Table/cells/DebitCreditCell';
import AmountCell from '@tcs/client-portal-common/components/molecules/Table/cells/AmountCell';
import UnitDebitCreditCell from '@tcs/client-portal-common/components/molecules/Table/cells/UnitDebitCreditCell';
import { useTheme } from '@forgeglobal/design-framework';

interface Props {
  transactions: Transaction[];
  showBalance: boolean;
  showUnits: boolean;
  loading: boolean;
}

const AccountTransactionsTable = ({
  transactions,
  showBalance,
  showUnits,
  loading,
}: Props) => {
  const theme = useTheme();

  const columns = [
    {
      accessor: 'date',
      header: () => <BaseHeader text="Date" />,
      cell: (info) => <DateCell value={info.getValue()} />,
      size: 160,
    },
    {
      accessor: 'description',
      header: () => <BaseHeader text="Description" />,
      cell: (info) => (
        <TwoLineCell
          primary={info.getValue().primary}
          secondary={info.getValue().secondary}
          isPending={info.row.original?.pending} // use pending colors (see depositCss)
        />
      ),
      size: 300,
    },
    ...(showUnits
      ? [
          {
            accessor: 'units',
            header: () => <BaseHeader text="Units" />,
            cell: (info) => <UnitDebitCreditCell value={info.getValue()} />,
            size: 200,
          },
        ]
      : []),
    {
      accessor: 'amount',
      header: () => <BaseHeader text={showUnits ? 'Cash Amount' : 'Amount'} />,
      cell: (info) => <DebitCreditCell value={info.getValue()} />,
      size: 200,
    },
    ...(showBalance
      ? [
          {
            accessor: 'balance',
            header: () => <BaseHeader text="Balance" />,
            cell: (info) => <AmountCell value={info.getValue()} />,
            size: 200,
          },
        ]
      : []),
  ];

  const displayTransactions = transformDebitCredit(transactions || []);

  // custom row styling if deposit has not cleared
  const depositCss = function (row) {
    return row.original?.pending ? { color: theme.palette.text.disabled } : {};
  };

  return (
    <Table
      columns={columns}
      customRowCssFunction={depositCss}
      data={displayTransactions}
      loading={loading}
    />
  );
};

export default AccountTransactionsTable;
